import { m } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { SearchX } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface DataNotFoundProps {
  /** Defaults to "No results found". */
  title?: string;
  /** Defaults to a generic "try adjusting your search/filters" hint; pass
   *  null to show the title alone. */
  description?: string | null;
  /** Defaults to a search-with-x icon. */
  icon?: LucideIcon;
  /** Smaller padding/min-height for embedding inside a modal, card, or
   * table body instead of standing in for a whole page/section. */
  compact?: boolean;
  className?: string;
  /** Optional slot for a "Clear filters" / "Reset search" button. */
  action?: ReactNode;
  /** No card chrome (border, background, shadow), for use inside a surface
   *  that already has its own, such as a chart card. */
  bare?: boolean;
  /** One small line (icon and title), for tight spots such as a dropdown
   *  menu or a log panel. Ignores description and action. */
  inline?: boolean;
}

/**
 * Generic empty-state — no results for a search/filter, or any other spot
 * where a list/lookup legitimately came back with nothing. Not for "this
 * feature isn't built yet" (see UnderDevelopment) or "the request failed"
 * (that's a real error, not an empty state) — this is specifically for a
 * successful lookup that found zero matching data.
 */
export function DataNotFound({
  title,
  description,
  icon,
  compact = false,
  className = "",
  action,
  bare = false,
  inline = false,
}: DataNotFoundProps = {}) {
  const Icon = icon ?? SearchX;
  const { t } = useTranslation("common");

  if (inline) {
    return (
      <div
        role="status"
        className={`flex items-center justify-center gap-2 px-3 py-3 text-xs text-[rgb(var(--text-muted))] ${className}`}
      >
        <Icon size={14} className="shrink-0 opacity-70" aria-hidden />
        <span>{title ?? t("titleDataNotFound")}</span>
      </div>
    );
  }

  return (
    <m.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        flex flex-col
        items-center
        justify-center
        ${compact ? "min-h-[160px] p-6" : "min-h-[320px] p-8"}
        text-center
        ${bare ? "" : "bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-2xl shadow-lifted"}
        ${className}
      `}
    >
      <div
        className={`
          ${compact ? "p-3 mb-3" : "p-4 mb-4"}
          bg-[rgb(var(--card-muted))]
          rounded-full
          border border-[rgb(var(--border))]
          text-[rgb(var(--text-muted))]
        `}
      >
        <Icon size={compact ? 26 : 36} />
      </div>

      <h3
        className={`
          font-semibold
          text-[rgb(var(--text-primary))]
          ${compact ? "text-base mb-1" : "text-lg mb-1.5"}
        `}
      >
        {title ?? t("titleDataNotFound")}
      </h3>

      {description !== null && (
        <p
          className={`
          text-[rgb(var(--text-muted))]
          max-w-sm
          leading-relaxed
          ${compact ? "text-xs" : "text-sm"}
        `}
        >
          {description ?? t("descriptionDataNotFound")}
        </p>
      )}

      {action && <div className="mt-4">{action}</div>}
    </m.div>
  );
}

/** A table's "no rows" row: the shared empty state across every column. */
export function EmptyTableRow({
  colSpan,
  ...props
}: DataNotFoundProps & { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="p-0">
        <DataNotFound compact bare {...props} />
      </td>
    </tr>
  );
}
